import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {UserCareProgramWeek} from '@shared/api/models/query/user-care-program.model';

export interface WeekAdherenceEntry {
    chapterAdherence: number | null;
    checkinAdherence: number | null;
}

export interface VisibleWeekRange {
    from: number;
    to: number;
}

const WINDOW_RADIUS = 2;

@Component({
    selector: 'cdss-week-card-selector',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './week-card-selector.component.html',
    styleUrl: './week-card-selector.component.scss'
})
export class WeekCardSelectorComponent implements OnInit, OnChanges {
    @Input() weeks: UserCareProgramWeek[] = [];
    @Input() selectedWeekNumber: number | null = null;
    @Input() adherenceByWeek: Record<number, WeekAdherenceEntry> = {};
    @Input() loadingWeekNumbers: number[] = [];

    @Output() weekSelected = new EventEmitter<number>();
    @Output() visibleRangeChanged = new EventEmitter<VisibleWeekRange>();

    private windowCenter: number | null = null;
    private initialized = false;

    ngOnInit(): void {
        this.initializeWindowIfReady();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.initializeWindowIfReady();
    }

    private initializeWindowIfReady(): void {
        // Wait for the weeks: the parent's selectedWeekNumber holds a placeholder
        // default until the care programme loads, and centering on it would pin
        // the window to the first weeks instead of the real selected week.
        if (this.windowCenter === null && this.selectedWeekNumber !== null && this.weeks.length) {
            this.windowCenter = this.selectedWeekNumber;
        }
        if (!this.initialized && this.weeks.length && this.windowCenter !== null) {
            this.initialized = true;
            this.emitVisibleRange();
        }
    }

    get visibleWeeks(): UserCareProgramWeek[] {
        if (this.windowCenter === null) return [];
        const minNumber = Math.min(...this.weeks.map(w => w.number));
        const maxNumber = Math.max(...this.weeks.map(w => w.number));
        const from = Math.max(minNumber, Math.min(this.windowCenter - WINDOW_RADIUS, maxNumber - WINDOW_RADIUS * 2));
        const to = Math.min(maxNumber, from + WINDOW_RADIUS * 2);
        return this.weeks.filter(w => w.number >= from && w.number <= to);
    }

    selectWeek(weekNumber: number): void {
        this.weekSelected.emit(weekNumber);
    }

    shiftWindow(direction: 1 | -1): void {
        if (this.windowCenter === null || !this.weeks.length) return;
        const minNumber = Math.min(...this.weeks.map(w => w.number));
        const maxNumber = Math.max(...this.weeks.map(w => w.number));
        const nextCenter = this.windowCenter + direction;
        if (nextCenter < minNumber || nextCenter > maxNumber) return;
        this.windowCenter = nextCenter;
        this.emitVisibleRange();
        // The window is now centered on nextCenter — treat navigation as also
        // selecting that week, so the active card always stays inside the
        // visible window and the panels below always match what's shown.
        this.weekSelected.emit(this.windowCenter);
    }

    isWeekLoading(weekNumber: number): boolean {
        return this.loadingWeekNumbers.includes(weekNumber);
    }

    adherenceFor(weekNumber: number): WeekAdherenceEntry | null {
        return this.adherenceByWeek[weekNumber] ?? null;
    }

    private emitVisibleRange(): void {
        const visible = this.visibleWeeks;
        if (!visible.length) return;
        this.visibleRangeChanged.emit({
            from: visible[0].number,
            to: visible[visible.length - 1].number
        });
    }
}
